import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Loader, Save, ArrowLeft } from 'lucide-react';
import { ref, get, update } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../../shared/firebase';
import { ALL_PROVINCES } from '../utils/provinces';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const INSTRUMENTS = ['Zang', 'Gitaar', 'Basgitaar', 'Drums', 'Keyboard', 'Piano', 'Saxofoon', 'Trompet', 'Viool', 'Cello', 'DJ', 'Productie', 'Anders'];
const GENRES = ['Rock', 'Pop', 'Metal', 'Jazz', 'Blues', 'Funk', 'Soul', 'R&B', 'Hip-hop', 'Electronic', 'Folk', 'Country', 'Klassiek', 'Punk', 'Indie', 'Reggae'];

export default function EditAd() {
  const { adId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', lookingFor: [], genres: [], city: '', province: ''
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) { navigate('/inloggen'); return; }
      setUser(currentUser);
      
      const adSnap = await get(ref(database, `vjb_ads/${adId}`));
      if (adSnap.exists()) {
        const data = adSnap.val();
        if (data.createdBy !== currentUser.uid) { navigate('/profiel'); return; }
        setForm({
          title: data.title || '', description: data.description || '',
          lookingFor: data.lookingFor || [], genres: data.genres || [],
          city: data.location?.city || '', province: data.location?.province || ''
        });
      } else {
        navigate('/profiel');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [adId, navigate]);

  const toggleArrayItem = (field, item) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(item) ? prev[field].filter(i => i !== item) : [...prev[field], item]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    try {
      await update(ref(database, `vjb_ads/${adId}`), {
        title: form.title, description: form.description,
        lookingFor: form.lookingFor, genres: form.genres,
        location: { city: form.city, province: form.province },
        updatedAt: new Date().toISOString()
      });
      navigate(`/oproep/${adId}`);
    } catch (error) {
      console.error('Error updating ad:', error);
      alert('Er ging iets mis');
    }
    setSaving(false);
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader className="animate-spin text-[#8146FF]" size={32} /></div>;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />
      <main className="flex-1 max-w-2xl mx-auto px-4 md:px-6 py-8 w-full">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/profiel" className="p-2 -ml-2 text-white/70 hover:text-white transition">
            <ArrowLeft size={24} />
          </Link>
          <h1 className="text-2xl font-bold">Oproep bewerken</h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="font-semibold mb-4">Titel *</h2>
            <input type="text" required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} maxLength={100}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#8146FF]" />
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="font-semibold mb-4">Wat zoek je? *</h2>
            <div className="flex flex-wrap gap-2">
              {INSTRUMENTS.map(inst => (
                <button key={inst} type="button" onClick={() => toggleArrayItem('lookingFor', inst)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition ${form.lookingFor.includes(inst) ? 'bg-[#8146FF] text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}>{inst}</button>
              ))}
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="font-semibold mb-4">Genres *</h2>
            <div className="flex flex-wrap gap-2">
              {GENRES.map(genre => (
                <button key={genre} type="button" onClick={() => toggleArrayItem('genres', genre)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition ${form.genres.includes(genre) ? 'bg-[#8146FF] text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}>{genre}</button>
              ))}
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="font-semibold mb-4">Locatie *</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-white/70 mb-2">Stad</label>
                <input type="text" required value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#8146FF]" />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-2">Provincie</label>
                <select required value={form.province} onChange={(e) => setForm({...form, province: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#8146FF]">
                  <option value="" className="bg-gray-900">Kies provincie</option>
                  {ALL_PROVINCES.map(p => <option key={p.code} value={p.code} className="bg-gray-900">{p.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="font-semibold mb-4">Beschrijving *</h2>
            <textarea required value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={5} maxLength={2000}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#8146FF] resize-none" />
            <p className="text-xs text-white/40 mt-1 text-right">{form.description.length}/2000</p>
          </div>

          <button type="submit" disabled={saving || !form.title || !form.description || form.lookingFor.length === 0 || form.genres.length === 0 || !form.city || !form.province}
            className="w-full bg-[#8146FF] text-white py-4 rounded-full font-semibold hover:bg-[#6a35d9] transition disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader className="animate-spin" size={20} /> : <Save size={20} />} {saving ? 'Opslaan...' : 'Wijzigingen opslaan'}
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
}
